import React, { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"

import { DictionaryTermApiProvider, useDictionaryTermApi } from "../Services/DictionaryTermApiProvider"
import { AlertProvider, useAlert } from "../Components/AlertContext"
import { ConfirmationProvider } from "../Components/ConfirmationContext"
import Breadcrumbs from "../Components/Breadcrumbs"
import { CustomInput, CustomToggle, CustomTextarea } from "../Components/CustomFormElements"
import Button from "../Components/Buttons"
import PageLoader from "../Components/PageLoader"

function DictionaryTermDetailContent() {
  const { dictionaryId, termId } = useParams()
  const isNew = termId === "new"
  const navigate = useNavigate()
  const { showAlert } = useAlert()

  const { getDictionaryTermById, updateDictionaryTerm, createDictionaryTerm } = useDictionaryTermApi()

  const [term, setTerm] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (isNew) {
      setTerm({ keyword: "", definition: "", is_active: true })
      setLoading(false)
    } else {
      const fetchTerm = async () => {
        try {
          const data = await getDictionaryTermById(dictionaryId, termId)
          setTerm(data)
        } catch (error) {
          console.error("Failed to load term", error)
        } finally {
          setLoading(false)
        }
      }
      fetchTerm()
    }
  }, [dictionaryId, termId])

  const handleSave = async () => {
    setSaving(true)
    try {
      if (isNew) {
        await createDictionaryTerm(dictionaryId, {
          keyword: term.keyword,
          definition: term.definition,
          is_active: term.is_active
        })
        showAlert("success", "Term successfully created!")
      } else {
        await updateDictionaryTerm(dictionaryId, termId, {
          keyword: term.keyword,
          definition: term.definition,
          is_active: term.is_active
        })
        showAlert("success", "Term successfully updated!")
      }
      navigate(-1)
    } catch (error) {
      console.error("Error saving term", error)
      showAlert("fail", "Failed to save term")
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <PageLoader />
  if (!term) return <div className="container">Term not found</div>

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <h2 className="h2-title">{isNew ? "Create Term" : "Edit Term"}</h2>
          <div className='gap d-flex flex-row-to-col flex-column'>
            <div className='int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr'>
              <h4 className='h4-title'>Main Settings</h4>

              <div className='input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Keyword</div>
                  <div className='small-font'>Enter term keyword</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomInput
                    id="keyword"
                    label="Keyword"
                    value={term.keyword}
                    placeholder="Enter keyword"
                    onChange={(id, val) => setTerm(prev => ({ ...prev, keyword: val }))}
                  />
                </div>
              </div>

              <div className='input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Definition</div>
                  <div className='small-font'>Enter term definition</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomTextarea
                    id="definition"
                    label="Definition"
                    name="definition"
                    addedText={term.definition}
                    onChange={(val) => setTerm(prev => ({ ...prev, definition: val }))}
                    resizable={false}
                    height="80px"
                  />
                </div>
              </div>

              <div className='input-row input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Activate Term</div>
                  <div className='small-font'>Set term to be active or inactive</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomToggle
                    id="toggle-status"
                    label={term.is_active ? 'Active' : 'Inactive'}
                    checked={term.is_active}
                    onChange={(id, value) => setTerm(prev => ({ ...prev, is_active: value }))}
                  />
                </div>
              </div>

            </div>

            <div className='buttons-container d-flex justify-end'>
              <Button variant='primary' size="medium" onClick={handleSave} disabled={saving}>
                Save
              </Button>
              <Button variant="danger" size="medium" onClick={() => navigate(-1)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function DictionaryTermDetail() {
  return (
    <AlertProvider>
      <ConfirmationProvider>
        <DictionaryTermApiProvider>
          <DictionaryTermDetailContent />
        </DictionaryTermApiProvider>
      </ConfirmationProvider>
    </AlertProvider>
  )
}

export default DictionaryTermDetail
